import { findElementByDomUniquePath } from "@/components/Canvas/utils/utils";
import {
  useCanvasStore,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import {
  CSS_VALUES_TO_COLLECT,
  CSS_VALUES_TO_COLLECT_FOR_PARENT,
} from "@/utils/constants";
import { NavTreeNode } from "@/utils/types";
import { RefObject, useEffect } from "react";

interface UseExtractCssProps {
  storyboardId: string;
  iframeDocRef: RefObject<Document>;
}

export interface CssEvals {
  [key: string]: string | CssEvals;
  parent: CssEvals;
}

export const useExtractCss = ({
  storyboardId,
  iframeDocRef,
}: UseExtractCssProps) => {
  const { selectedNavTreeNode } = useSelectedNavTreeNode();
  const instantChangeQueue = useDesignCommandManager(
    (state) => state.instantChangeQueue,
  );
  const tempDraggingStoryboard = useCanvasStore(
    (state) => state.temporaryStoryboard,
  );

  useEffect(() => {
    if (!iframeDocRef.current) {
      return;
    }

    // Do not change
    if (tempDraggingStoryboard) {
      return;
    }

    if (!selectedNavTreeNode) {
      useCanvasStore
        .getState()
        .setSelectedElementCssEvals(undefined, undefined);
      return;
    }

    if (selectedNavTreeNode.tempoElement.storyboardId !== storyboardId) {
      return;
    }

    const { cssEvals, classList } = getCssEvals(
      iframeDocRef.current,
      selectedNavTreeNode,
    );

    useCanvasStore.getState().setSelectedElementCssEvals(cssEvals, classList);
  }, [
    selectedNavTreeNode,
    iframeDocRef,
    storyboardId,
    instantChangeQueue,
    tempDraggingStoryboard,
  ]);
};

export const cssEval = (
  iframeDoc: Document | null,
  element: HTMLElement,
  property: string,
): string | undefined => {
  return iframeDoc?.defaultView
    ?.getComputedStyle(element, null)
    .getPropertyValue(property);
};

const getCssEvals = (
  iframeDoc: Document,
  selectedNavTreeNode: NavTreeNode,
): {
  cssEvals?: CssEvals;
  classList?: string[];
} => {
  const cssEvals: any = {};

  const selectedDomElement = findElementByDomUniquePath(
    iframeDoc,
    selectedNavTreeNode.domUniquePath ??
      selectedNavTreeNode.domUniquePathForComponent,
  );
  if (!selectedDomElement) {
    return {
      cssEvals: undefined,
      classList: undefined,
    };
  }

  CSS_VALUES_TO_COLLECT.forEach((cssName: string) => {
    cssEvals[cssName] = cssEval(iframeDoc, selectedDomElement, cssName);
  });

  const parentCssEvals: any = {};

  const parentElement = selectedDomElement.parentElement;

  if (parentElement) {
    CSS_VALUES_TO_COLLECT_FOR_PARENT.forEach((cssName: string) => {
      parentCssEvals[cssName] = cssEval(iframeDoc, parentElement, cssName);
    });
  }

  cssEvals["parent"] = parentCssEvals;

  return {
    cssEvals,
    classList: Array.from(selectedDomElement.classList),
  };
};
